import React, { useMemo } from "react"
import {
  Box,
  Grid,
  Skeleton,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material"
import MainCard from "./MainCard"
import sortBy from "lodash/sortBy"
import { BorderlessLinearProgressWithLabel } from "shared/ui/berry-jass/Progress"
import times from "lodash/times"
import { formatPriceWithCents } from "shared"

const PercentageDataTable = ({
  title,
  data = [],
  loading,
  loadingSize = 2,
  blankContent,
  showTotalAsPrice,
  headerSX = {},
  contentSX = {},
  getBarColor,
  barTrackColor,
  barThickness,
  ...rest
}) => {
  const theme = useTheme()
  const sortedPercentageData = useMemo(
    () =>
      sortBy(
        data.map((d) => d.percentage),
        (d) => -d
      ),
    [data]
  )

  const base = sortedPercentageData?.[0] ?? 1

  const getRankColor = (value) => {
    if (value >= sortedPercentageData[0]) {
      return theme.palette.primary.main
    } else if (value >= sortedPercentageData[1]) {
      return "#373749"
    } else if (value >= sortedPercentageData[2]) {
      return "#5E6077"
    } else if (value >= sortedPercentageData[3]) {
      return "#7B7D92"
    }
    return "#999CAE"
  }

  const colorForBar = (row, index) =>
    getBarColor ? getBarColor(row, index) : getRankColor(row.percentage)

  return (
    <MainCard
      dividerPadding
      border={false}
      title={title}
      contentSX={{
        pt: 1,
        pb: "20px !important",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        ...contentSX
      }}
      headerSX={{
        px: 2,
        pt: 2.5,
        pb: 1,
        ...headerSX
      }}
      {...rest}
    >
      <Grid container spacing={1}>
        {loading &&
          times(loadingSize, (i) => {
            return (
              <Grid key={i} item xs={12}>
                <Grid
                  container
                  alignItems="center"
                  columnSpacing={1}
                  rowSpacing={0}
                >
                  <Grid item xs zeroMinWidth>
                    <Skeleton width={70} />
                  </Grid>
                  <Grid item>
                    <Skeleton width={40} />
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton height={20} />
                  </Grid>
                </Grid>
              </Grid>
            )
          })}
        {!loading &&
          data.map((row, i) => {
            const { percentage, name, total } = row
            const scaledValue = (percentage / base) * 100
            return (
              // index is used as a key here to provide smooth animations on data change
              <Grid key={i} item xs={12}>
                <Grid
                  container
                  alignItems="center"
                  columnSpacing={1}
                  rowSpacing={0}
                >
                  <Grid item xs zeroMinWidth>
                    <Tooltip placement="top" title={name}>
                      <Typography variant="h6" noWrap>
                        {name}
                      </Typography>
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Typography variant="h4" align="right">
                      {showTotalAsPrice ? formatPriceWithCents(total) : total}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {percentage && (
                      <BorderlessLinearProgressWithLabel
                        value={scaledValue}
                        label={`${percentage.toFixed()}%`}
                        color={colorForBar(row, i)}
                        trackColor={barTrackColor}
                        thickness={barThickness}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )
          })}
      </Grid>
      {!loading && !data.length && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          flexGrow={1}
          minHeight={100}
        >
          <Typography
            align="center"
            sx={{ fontSize: "1rem", color: "grey.600", fontWeight: 500 }}
          >
            {blankContent}
          </Typography>
        </Box>
      )}
    </MainCard>
  )
}

export default PercentageDataTable
